import React from 'react';
import { css } from '@emotion/react';
import { useNotificationPreferences } from '../../../hooks/useNotificationPreferences';
import { colors, spacing, fontStyles } from '../../../styles';
import iconImage from '../../ChatPage/icon.webp';
import NotificationBlockedModal from '../../NotificationBlockedModal';
import ToggleSwitch from '../../ToggleSwitch';
import * as stores from '../../../stores';

// Suggested notifications — most users benefit from these
const SUGGESTED_NOTIFICATIONS = ['smart_notifications', 'news_alerts', 'daily_alerts', 'ai_portfolio_trades'];

// Mock notification examples
interface NotificationExample {
  symbol?: string;
  content: string;
  time: string;
  dot?: string;
  additionalInfo?: string;
}

// Notification examples that match the preferences
const notificationExamples: Record<string, NotificationExample> = {
  smart_notifications: {
    symbol: 'Tech Rally Continues',
    content:
      'S&P 500 up 1.2% with tech leading gains. NVIDIA hits new highs on AI demand, while energy stocks pull back on oil concerns.',
    time: '8:00 AM',
  },
  daily_alerts: {
    symbol: 'AAPL',
    content: 'Apple is up 4% today.',
    time: '6m ago',
    dot: '#4CAF50', // Green
  },
  weekly_alerts: {
    symbol: 'NKE',
    content: 'Nike is down -8% in the past 7 days.',
    time: '2hr ago',
    dot: '#F44336', // Red
  },
  news_alerts: {
    symbol: 'News on QQQ, SPY',
    content: 'Major indexes are higher amid US-Russia talks.',
    time: '27m ago',
  },
  insider_sales: {
    symbol: '‼️ ACME insider sells $375M',
    content: 'ACME president has sold $375M in shares in the last 3 months.',
    time: '27m ago',
  },
  rsi_alerts: {
    symbol: '🟡 AAPL RSI 70',
    content: 'Overbought signal — consider rebalancing or taking profits.',
    time: '27m ago',
  },
  sma_alerts: {
    symbol: '🔴 AAPL below 50d avg',
    content: "AAPL's stock price crossed below the 50d SMA at $220.93, a bearish signal.",
    time: '27m ago',
  },
  insight_alerts: {
    symbol: 'Amazon Q3 2024 Earnings',
    content:
      'Amazon reports strong Q3 with improved profitability across segments, AWS acceleration, and significant free cash flow generation.',
    time: '27m ago',
  },
  earnings_alerts: {
    symbol: '📈 NFLX Q2 2022 Earnings Summary',
    content: 'Netflix reported a Q2 2022 subscriber loss of 970,000, better than the expected 2 million decline...',
    time: '27m ago',
  },
  ai_portfolio_trades: {
    symbol: '🤖 Claude bought NVDA',
    content: 'Claude AI portfolio added NVIDIA — up 8% this week. GPT and Gemini portfolios also hold this position.',
    time: '1h ago',
    dot: '#D97757',
  },
};

// Generic fallback example for new notification types that don't have specific examples yet
const getNotificationExample = (prefId: string, description: string): NotificationExample => {
  const example = notificationExamples[prefId];

  if (example) {
    return example;
  }

  // Fallback example for new notification types
  console.warn(`[ONBOARDING] No example found for notification type: ${prefId}, using generic example`);

  return {
    symbol: 'AAPL',
    content: `Example notification for: ${description}`,
    time: '10m ago',
    dot: '#2196F3', // Blue
  };
};

const NotificationCard: React.FC<{ example: NotificationExample }> = ({ example }) => {
  return (
    <div
      css={css`
        background-color: ${colors.sugar};
        border-radius: 12px;
        padding: ${spacing.centi};
        margin-bottom: ${spacing.centi};
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
        transition: all 0.3s ease;
        position: relative;

        html[data-theme='dark'] & {
          background-color: ${colors.liquorice};
        }
      `}
    >
      {/* Time in top right */}
      <div
        css={css`
          position: absolute;
          top: ${spacing.centi};
          right: ${spacing.centi};
          ${fontStyles.flea};
          color: ${colors.cloud};
          font-weight: normal;
        `}
      >
        {example.time}
      </div>

      <div
        css={css`
          display: flex;
          align-items: center;
        `}
      >
        <div
          css={css`
            margin-right: ${spacing.centi};
            flex-shrink: 0;
          `}
        >
          <div
            css={css`
              width: 40px;
              height: 40px;
              border-radius: 50%;
              background-color: white;
              display: flex;
              align-items: center;
              justify-content: center;
              overflow: hidden;
              border: 1px solid #eeeeee;
            `}
          >
            <img
              src={iconImage}
              alt="Bloom"
              css={css`
                width: 30px;
                height: 30px;
                object-fit: contain;
              `}
            />
          </div>
        </div>
        <div
          css={css`
            flex: 1;
            padding-right: 60px; /* Make room for the time */
          `}
        >
          {example.symbol && (
            <div
              css={css`
                display: flex;
                align-items: center;
                margin-bottom: 1px;
              `}
            >
              {example.dot && (
                <span
                  css={css`
                    display: inline-block;
                    width: 10px;
                    height: 10px;
                    border-radius: 50%;
                    background-color: ${example.dot};
                    margin-right: 6px;
                  `}
                ></span>
              )}
              <span
                css={css`
                  ${fontStyles.mouse};
                  font-weight: 700;
                  color: ${colors.liquorice};
                  html[data-theme='dark'] & {
                    color: ${colors.sugar};
                  }
                `}
              >
                {example.symbol}
              </span>
            </div>
          )}
          <div
            css={css`
              ${fontStyles.mouse};
              color: ${colors.liquorice};
              font-weight: normal;
              text-align: left;
              html[data-theme='dark'] & {
                color: ${colors.sugar};
              }
            `}
          >
            {example.content}
          </div>
          {example.additionalInfo && (
            <div
              css={css`
                ${fontStyles.flea};
                color: ${colors.liquorice};
                font-weight: normal;
                text-align: left;
                html[data-theme='dark'] & {
                  color: ${colors.sugar};
                }
              `}
            >
              {example.additionalInfo}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface OnboardingNotificationProps {
  /** If provided, personalizes the sample notifications for this ticker */
  topSymbol?: string;
}

const OnboardingNotification: React.FC<OnboardingNotificationProps> = ({ topSymbol }) => {
  const {
    preferences,
    handleToggle,
    isNotificationBlockedModalOpen,
    setIsNotificationBlockedModalOpen,
    hasReachedFreeSummaryLimit,
  } = useNotificationPreferences();

  // Check if user is Pro (needed for hasReachedFreeSummaryLimit logic)
  const purchaserInfo = stores.Purchase.useState((s) => s.purchaserInfo);
  const isPro = purchaserInfo?.entitlements?.active[stores.entitlementIdentifier]?.isActive;

  // Split preferences into suggested and power user (everything else)
  const suggestedPrefs = preferences.filter((pref) => SUGGESTED_NOTIFICATIONS.includes(pref.id));
  const powerUserPrefs = preferences.filter((pref) => !SUGGESTED_NOTIFICATIONS.includes(pref.id));

  const renderPreferenceCard = (pref: (typeof preferences)[0]) => {
    let example = getNotificationExample(pref.id, pref.description);

    // Personalize sample notifications with the user's top stock
    if (topSymbol) {
      if (pref.id === 'daily_alerts') {
        example = { ...example, symbol: topSymbol, content: `${topSymbol} is up 4% today.`, dot: '#4CAF50' };
      } else if (pref.id === 'news_alerts') {
        example = {
          ...example,
          symbol: `News on ${topSymbol}`,
          content: `Breaking news affecting ${topSymbol} — tap to read more.`,
        };
      } else if (pref.id === 'ai_portfolio_trades') {
        example = {
          ...example,
          symbol: `🤖 Claude bought ${topSymbol}`,
          content: `Claude AI portfolio added ${topSymbol} — see why our AI just bought it.`,
        };
      }
    }
    const isSmartNotification = pref.id === 'smart_notifications';
    const hasReachedLimit =
      isSmartNotification && !isPro && hasReachedFreeSummaryLimit ? hasReachedFreeSummaryLimit() : false;

    return (
      <div
        key={pref.id}
        css={css`
          padding: ${spacing.deci} ${spacing.deci} ${spacing.centi};
          background-color: var(--card-background);
          border-radius: 8px;
          opacity: ${hasReachedLimit ? 0.6 : 1};
          position: relative;
        `}
      >
        <div
          css={css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: ${spacing.deci};
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: ${spacing.milli};
              flex-wrap: wrap;
            `}
          >
            <p
              css={css`
                ${fontStyles.dog};
                font-weight: 500;
              `}
            >
              {pref.description}
            </p>
          </div>
          <ToggleSwitch checked={pref.enabled} onChange={() => void handleToggle(pref.id)} disabled={hasReachedLimit} />
        </div>

        {/* Embedded example notification */}
        <NotificationCard example={example} />
      </div>
    );
  };

  return (
    <div
      css={css`
        width: 100%;
        display: flex;
        flex-direction: column;
        margin-top: ${spacing.deci};
      `}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: ${spacing.deci};
        `}
      >
        {/* Suggested notifications */}
        {suggestedPrefs.length > 0 && (
          <p
            css={css`
              ${fontStyles.lion};
              color: ${colors.tungsten};
            `}
          >
            Suggested for you
          </p>
        )}
        {suggestedPrefs.map(renderPreferenceCard)}

        {/* Power user notifications */}
        {powerUserPrefs.length > 0 && (
          <p
            css={css`
              ${fontStyles.lion};
              color: ${colors.tungsten};
              margin-top: ${spacing.kilo};
            `}
          >
            For power users
          </p>
        )}
        {powerUserPrefs.map(renderPreferenceCard)}
      </div>

      <NotificationBlockedModal
        isOpen={isNotificationBlockedModalOpen}
        onClose={() => setIsNotificationBlockedModalOpen(false)}
      />
    </div>
  );
};

export default OnboardingNotification;
